import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { MarketCalendar, recordDateEvent } from "@yct/core";
import { categoryLabel, data, recordDayLabel, restrictionLevel, type RecordDateEntry, type RestrictionEntry, type StockRow, type YutaiIndexEntry } from "../lib/data.js";
import { getViewHistory } from "../lib/view-history.js";

const cal = new MarketCalendar();
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/** Hiragana -> katakana, full-width -> half-width, lower-case; used for loose matching. */
const norm = (s: string): string =>
  s
    .replace(/[ぁ-ん]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 0x60))
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .toLowerCase();

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const crossDay = params.get("lastTradingDay"); // S-4 mode
  const [q, setQ] = useState(params.get("q") ?? "");
  const [months, setMonths] = useState<number[]>([]);
  const [dayType, setDayType] = useState<"" | "month_end" | "mid">("");
  const [category, setCategory] = useState("");
  const [onlyYutai, setOnlyYutai] = useState(!!crossDay);

  const [stocks, setStocks] = useState<StockRow[]>([]);
  const [yutai, setYutai] = useState<YutaiIndexEntry[]>([]);
  const [prices, setPrices] = useState<Record<string, { date: string; close: number }>>({});
  const [restrictions, setRestrictions] = useState<Record<string, RestrictionEntry[]>>({});
  const [crossEvents, setCrossEvents] = useState<RecordDateEntry[] | null>(null);

  useEffect(() => {
    data.stocks().then(setStocks);
    data.yutaiIndex().then(setYutai);
    data.prices().then((p) => setPrices(p.prices));
    data.restrictions().then(setRestrictions);
  }, []);

  useEffect(() => {
    if (!crossDay) {
      setCrossEvents(null);
      return;
    }
    data.recordDates(crossDay.slice(0, 7)).then((ev) => setCrossEvents(ev.filter((e) => e.lastTradingDay === crossDay)));
  }, [crossDay]);

  const yutaiByCode = useMemo(() => new Map(yutai.map((y) => [y.code, y])), [yutai]);
  const stockByCode = useMemo(() => new Map(stocks.map((s) => [s.code, s])), [stocks]);

  const results = useMemo(() => {
    const nq = norm(q.trim());
    let list: Array<{ code: string; name: string }> = crossEvents
      ? crossEvents.map((e) => ({ code: e.code, name: stockByCode.get(e.code)?.name ?? "" }))
      : stocks.map((s) => ({ code: s.code, name: s.name }));
    if (stocks.length === 0 && !crossEvents) {
      // No stocks master yet: fall back to the yutai index so the page is still usable.
      list = yutai.map((y) => ({ code: y.code, name: "" }));
    }
    return list
      .filter((s) => {
        if (nq && !norm(s.code).includes(nq) && !norm(s.name).includes(nq)) return false;
        const y = yutaiByCode.get(s.code);
        if (onlyYutai && !y) return false;
        if (months.length > 0 && (!y || !months.some((m) => y.recordMonths.includes(m)))) return false;
        if (dayType === "month_end" && (!y || y.recordDayType !== "month_end")) return false;
        if (dayType === "mid" && (!y || y.recordDayType === "month_end")) return false;
        if (category && (!y || !y.categoryTags.includes(category))) return false;
        return true;
      })
      .slice(0, 200);
  }, [q, stocks, yutai, yutaiByCode, stockByCode, crossEvents, onlyYutai, months, dayType, category]);

  const history = getViewHistory();
  const toggleMonth = (m: number) => setMonths((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]));

  return (
    <section>
      <h1>検索</h1>
      {crossDay && (
        <div className="card">
          <p>
            <strong>クロス予定モード</strong>: 権利付最終日 {crossDay} の銘柄だけを表示しています。
            <button className="btn link" type="button" onClick={() => setParams({})}>
              解除
            </button>
          </p>
          {crossEvents && crossEvents.length === 0 && <p className="muted">この日の権利付最終日に該当する優待銘柄はマスタに登録されていません。</p>}
        </div>
      )}
      <input className="input" type="search" inputMode="search" placeholder="証券コード・会社名" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="card form">
        <div className="row wrap">
          {MONTHS.map((m) => (
            <button key={m} type="button" className={`btn small ${months.includes(m) ? "primary" : ""}`} onClick={() => toggleMonth(m)}>
              {m}月
            </button>
          ))}
        </div>
        <div className="row wrap">
          <select className="input" value={dayType} onChange={(e) => setDayType(e.target.value as typeof dayType)}>
            <option value="">基準日: すべて</option>
            <option value="month_end">月末</option>
            <option value="mid">月中（15日・20日など）</option>
          </select>
          <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">優待種別: すべて</option>
            {Object.entries(categoryLabel).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <label className="check">
            <input type="checkbox" checked={onlyYutai} onChange={(e) => setOnlyYutai(e.target.checked)} /> 優待あり
          </label>
        </div>
      </div>
      {stocks.length === 0 && yutai.length === 0 && <p className="muted">銘柄マスタがまだ取り込まれていません。管理者が JPX の上場銘柄一覧を取り込むと検索できるようになります。</p>}
      <ul className="list">
        {results.map((s) => {
          const y = yutaiByCode.get(s.code);
          const lvl = restrictionLevel(restrictions[s.code]);
          const price = prices[s.code];
          return (
            <li key={s.code}>
              <Link to={`/stocks/${s.code}`} className="row between">
                <span>
                  <strong>{s.code}</strong> {s.name}
                  {y && (
                    <span className="muted">
                      {" "}
                      / {y.recordMonths.join("・")}月 {recordDayLabel[y.recordDayType] ?? ""} / {y.categoryTags.map((t) => categoryLabel[t] ?? t).join("・")}
                    </span>
                  )}
                  {lvl && <span className={`badge ${lvl}`}> {lvl === "stop" ? "停止" : "注意"}</span>}
                </span>
                <span className="muted">{price ? `${price.close.toLocaleString("ja-JP")}円` : ""}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      {q === "" && !crossDay && history.length > 0 && (
        <div className="card">
          <h2>閲覧履歴</h2>
          <ul className="list">
            {history.map((h) => (
              <li key={h.stockCode}>
                <Link to={`/stocks/${h.stockCode}`}>
                  {h.stockCode} {stockByCode.get(h.stockCode)?.name ?? ""}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {crossDay && (
        <p className="muted">
          参考: {crossDay} が権利付最終日のとき、権利確定日は {recordDateEvent(cal, cal.addBusinessDays(crossDay, 2)).recordDate} 付近です。
        </p>
      )}
    </section>
  );
}
